// Reusable primitives (ported from the design project)
import { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";
import { Sparkline } from "./charts";

/* ── Avatar ───────────────────────────────────────────────────────── */
const AV_COLORS: Record<string, string> = {
  SR: "#05686F", JC: "#16A34A", ML: "#e5844d", DP: "#2f6bff", AK: "#DC2626", TN: "#0891b2", BB: "#075869", AI: "#0A6D81",
};

export const Avatar = ({ name = "SR", size = 28, sq = false, color, ring }: {
  name?: string; size?: number; sq?: boolean; color?: string; ring?: boolean;
}) => {
  const bg = color || AV_COLORS[name] || "#6b7280";
  return (
    <span className={"av" + (sq ? " av-sq" : "")} style={{
      width: size, height: size, fontSize: size * 0.4, background: bg,
      boxShadow: ring ? `0 0 0 2px #fff, 0 0 0 3.5px ${bg}33` : "none",
    }}>{name}</span>
  );
};

/* ── Delta chip (▲/▼ %) ───────────────────────────────────────────── */
export const Delta = ({ v, invert = false, size = 12 }: { v: number; invert?: boolean; size?: number }) => {
  const up = v >= 0;
  const good = invert ? !up : up;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: size, fontWeight: 600, color: good ? "var(--pos)" : "var(--neg)" }}>
      <Icon name={up ? "arrow-up" : "arrow-down"} size={size} sw={2.2} />{Math.abs(v)}%
    </span>
  );
};

/* ── Segmented control ────────────────────────────────────────────── */
type SegOption = string | { value: string; label: string };

export const Segmented = ({ options, value, onChange, size = "md" }: {
  options: SegOption[]; value: string; onChange?: (v: string) => void; size?: "sm" | "md";
}) => (
  <div style={{ display: "inline-flex", background: "var(--surface-3)", borderRadius: 8, padding: 3, gap: 2 }}>
    {options.map((o) => {
      const v = typeof o === "string" ? o : o.value;
      const l = typeof o === "string" ? o : o.label;
      const on = v === value;
      return (
        <button key={v} onClick={() => onChange && onChange(v)} style={{
          border: "none", cursor: "pointer", font: "inherit", fontSize: size === "sm" ? 12 : 12.5,
          fontWeight: on ? 600 : 500, padding: size === "sm" ? "4px 9px" : "5px 12px", borderRadius: 6,
          background: on ? "#fff" : "transparent", color: on ? "var(--ink)" : "var(--ink-3)",
          boxShadow: on ? "var(--sh-sm)" : "none", transition: "all .12s",
        }}>{l}</button>
      );
    })}
  </div>
);

/* ── Tabs (underline) ─────────────────────────────────────────────── */
type TabOption = string | { value: string; label: string; badge?: number };

export const Tabs = ({ tabs, value, onChange }: { tabs: TabOption[]; value: string; onChange?: (v: string) => void }) => (
  <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--line)" }}>
    {tabs.map((t) => {
      const v = typeof t === "string" ? t : t.value;
      const label = typeof t === "string" ? t : t.label;
      const badge = typeof t === "string" ? undefined : t.badge;
      const on = v === value;
      return (
        <button key={v} onClick={() => onChange && onChange(v)} style={{
          border: "none", background: "none", cursor: "pointer", font: "inherit", fontSize: 13.5,
          fontWeight: on ? 600 : 500, color: on ? "var(--ink)" : "var(--ink-3)", padding: "9px 12px",
          position: "relative", display: "flex", alignItems: "center", gap: 7, transition: "color .12s",
        }}>
          {label}
          {badge != null && <span style={{
            minWidth: 17, height: 17, padding: "0 5px", borderRadius: 8, fontSize: 10.5, fontWeight: 700,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: on ? "var(--brand)" : "var(--surface-3)", color: on ? "#fff" : "var(--ink-3)",
          }}>{badge}</span>}
          {on && <span style={{ position: "absolute", left: 8, right: 8, bottom: -1, height: 2, borderRadius: 2, background: "var(--brand)" }} />}
        </button>
      );
    })}
  </div>
);

/* ── Hover-elevate helpers (replaces inline mouseenter handlers) ──── */
export const hoverLift = {
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.boxShadow = "var(--sh-md)";
    e.currentTarget.style.borderColor = "#C9DBE0";
  },
  onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.boxShadow = "none";
    e.currentTarget.style.borderColor = "var(--line)";
  },
};

/* ── KPI card ─────────────────────────────────────────────────────── */
export const Kpi = ({ label, value, delta, invert, spark, sparkColor = "var(--brand)", note, onClick }: {
  label: string; value: string; delta?: number; invert?: boolean; spark?: number[];
  sparkColor?: string; note?: string; onClick?: () => void;
}) => (
  <div className="card" style={{ padding: 16, cursor: onClick ? "pointer" : "default", transition: "box-shadow .15s,border-color .15s" }}
    onClick={onClick}
    onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.boxShadow = "var(--sh-md)"; e.currentTarget.style.borderColor = "#C9DBE0"; } }}
    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--line)"; }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-3)" }}>{label}</span>
      {delta != null && <Delta v={delta} invert={invert} />}
    </div>
    <div className="num" style={{ fontSize: 25, fontWeight: 600, letterSpacing: "-.02em", marginTop: 7, color: "var(--ink)" }}>{value}</div>
    {spark && <div style={{ marginTop: 10 }}><Sparkline data={spark} color={sparkColor} h={30} /></div>}
    {note && <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: spark ? 8 : 5 }}>{note}</div>}
  </div>
);

/* ── Object meta footer: comments · mentions · activity · assignee ── */
export const ObjectMeta = ({ comments = 0, mentions = 0, assignee, activity, compact }: {
  comments?: number; mentions?: number; assignee?: string; activity?: string; compact?: boolean;
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: compact ? 12 : 16, fontSize: 12, color: "var(--ink-4)" }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="comment" size={14} stroke="var(--ink-4)" />{comments}</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="at" size={14} stroke="var(--ink-4)" />{mentions}</span>
    {activity && <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="activity" size={14} stroke="var(--ink-4)" />{activity}</span>}
    {assignee && <span style={{ marginLeft: "auto" }}><Avatar name={assignee} size={22} /></span>}
  </div>
);

/* ── Section header (page-level) ──────────────────────────────────── */
export const SectionHead = ({ title, sub, actions, size = "md" }: {
  title: ReactNode; sub?: ReactNode; actions?: ReactNode; size?: "md" | "lg";
}) => (
  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
    <div>
      <div style={{ fontSize: size === "lg" ? 21 : 17, fontWeight: 600, letterSpacing: "-.015em", color: "var(--ink)" }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 3, maxWidth: 620 }}>{sub}</div>}
    </div>
    {actions && <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>{actions}</div>}
  </div>
);

/* ── Progress bar ─────────────────────────────────────────────────── */
export const Bar = ({ pct, color = "var(--brand)", h = 6, track = "var(--surface-3)" }: {
  pct: number; color?: string; h?: number; track?: string;
}) => (
  <div style={{ height: h, background: track, borderRadius: h, overflow: "hidden", width: "100%" }}>
    <div style={{ height: "100%", width: Math.max(0, Math.min(100, pct)) + "%", background: color, borderRadius: h }} />
  </div>
);

/* ── Search input ─────────────────────────────────────────────────── */
export const Search = ({ placeholder = "Search", w = 240 as number | string, value, onChange }: {
  placeholder?: string; w?: number | string; value?: string; onChange?: (v: string) => void;
}) => (
  <div style={{ position: "relative", width: w }}>
    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
      <Icon name="search" size={15} stroke="var(--ink-4)" />
    </span>
    <input value={value} onChange={(e) => onChange && onChange(e.target.value)} placeholder={placeholder} className="focus-ring" style={{
      width: "100%", height: 34, padding: "0 12px 0 32px", borderRadius: 8, border: "1px solid var(--line)",
      background: "var(--surface)", font: "inherit", fontSize: 13, color: "var(--ink)", outline: "none",
    }} />
  </div>
);

/* ── Toggle (visual) ──────────────────────────────────────────────── */
export const Toggle = ({ on, onClick }: { on: boolean; onClick?: () => void }) => (
  <span onClick={onClick} style={{
    width: 38, height: 22, borderRadius: 12, background: on ? "var(--brand)" : "var(--ink-5)",
    position: "relative", transition: "background .15s", flexShrink: 0, cursor: "pointer", display: "inline-block",
  }}>
    <span style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: 50, background: "#fff", boxShadow: "var(--sh-sm)", transition: "left .15s" }} />
  </span>
);

/* ── AI summary banner — every page starts with one ───────────────── */
export const AiSummary = ({ children, action, style }: { children: ReactNode; action?: ReactNode; style?: CSSProperties }) => (
  <div className="card ai-surface" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 16, ...style }}>
    <span className="ai-mark"><Icon name="spark" size={14} stroke="#fff" /></span>
    <div style={{ flex: 1, fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.55 }}>{children}</div>
    {action}
  </div>
);

export type CSS = CSSProperties;
